/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useMemo, useEffect, useState, Fragment, useRef } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-table components
import { useTable, usePagination, useGlobalFilter, useAsyncDebounce, useSortBy } from "react-table";

// @mui material components
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";
import Autocomplete from "@mui/material/Autocomplete";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Divider from "@mui/material/Divider";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDPagination from "components/MDPagination";

// Material Dashboard 2 React example components
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";

function DataTable({
  entriesPerPage,
  entriesPerPagePosition,
  canSearch,
  showTotalEntries,
  table,
  pagination,
  isSorted,
  noEndBorder,
  onRowClicked,
  getRowStyle,
  actions,
  dialogTitle,
  bodyMaxHeight,
  disableRowDialog,
  fixedLayout,
  showAllColumns,
}) {
  const defaultValue = entriesPerPage.defaultValue ? entriesPerPage.defaultValue : 20;
  const entries = entriesPerPage.entries
    ? entriesPerPage.entries.map((el) => el.toString())
    : ["20", "25", "30", "45", "50"];
  const columns = useMemo(() => table.columns, [table]);
  const data = useMemo(() => table.rows || [], [table]);

  const tableInstance = useTable(
    { columns, data, initialState: { pageIndex: 0 } },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    pageOptions,
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    state: { pageIndex, pageSize, globalFilter },
  } = tableInstance;

  // Set the default value for the entries per page when component mounts
  useEffect(() => setPageSize(defaultValue || 10), [defaultValue]);

  // Set the entries per page value based on the select value
  const setEntriesPerPage = (value) => setPageSize(value);

  // Render the paginations
  const renderPagination = pageOptions.map((option) => (
    <MDPagination
      item
      key={option}
      onClick={() => gotoPage(Number(option))}
      active={pageIndex === option}
    >
      {option + 1}
    </MDPagination>
  ));

  // Customized page options starting from 1
  const customizedPageOptions = pageOptions.map((option) => option + 1);

  // Setting value for the pagination input
  const handleInputPaginationValue = ({ target: value }) => gotoPage(Number(value.value - 1));

  // Search input value state
  const [search, setSearch] = useState(globalFilter);

  // Search input state handle
  const onSearchChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 100);

  // A function that sets the sorted value for the table
  const setSortedValue = (column) => {
    let sortedValue;

    if (isSorted && column.isSorted) {
      sortedValue = column.isSortedDesc ? "desc" : "asce";
    } else if (isSorted) {
      sortedValue = "none";
    } else {
      sortedValue = false;
    }

    return sortedValue;
  };

  // Setting the entries starting point
  const entriesStart = pageIndex === 0 ? pageIndex + 1 : pageIndex * pageSize + 1;

  // Setting the entries ending point
  let entriesEnd;

  if (pageIndex === 0) {
    entriesEnd = pageSize;
  } else if (pageIndex === pageOptions.length - 1) {
    entriesEnd = rows.length;
  } else {
    entriesEnd = pageSize * (pageIndex + 1);
  }

  const [dialogRow, setDialogRow] = useState(null);
  const tableContainerRef = useRef(null);
  const [maxVisibleColumns, setMaxVisibleColumns] = useState(6);

  // Calculate max visible columns based on screen width. Skipped entirely
  // when showAllColumns is set — callers with a small, fixed column count
  // (e.g. a 4-column table meant to always show every column) opt out of
  // this responsive column-hiding behaviour.
  useEffect(() => {
    if (showAllColumns) return undefined;

    const calculateMaxColumns = () => {
      const screenWidth = window.innerWidth;
      let maxCols;

      if (screenWidth >= 1920) {
        maxCols = 8; // Extra large screens
      } else if (screenWidth >= 1440) {
        maxCols = 7; // Large screens
      } else if (screenWidth >= 1024) {
        maxCols = 6; // Medium screens
      } else if (screenWidth >= 768) {
        maxCols = 4; // Tablet screens
      } else if (screenWidth >= 480) {
        maxCols = 3; // Small tablet/large mobile
      } else {
        maxCols = 2; // Mobile screens
      }

      setMaxVisibleColumns(maxCols);
    };

    // Calculate on mount
    calculateMaxColumns();

    // Add resize listener
    window.addEventListener('resize', calculateMaxColumns);

    // Cleanup
    return () => window.removeEventListener('resize', calculateMaxColumns);
  }, [showAllColumns]);

  // Columns flagged detailOnly are deliberately excluded from the grid (they
  // still render in the row-detail dialog below, which iterates all cells).
  // That's an explicit per-page choice, so it overrides the responsive
  // column-count auto-truncation rather than competing with it.
  const hasDetailOnlyColumns = useMemo(() => columns.some((column) => column.detailOnly), [columns]);
  const visibleColumns = useMemo(() => columns.filter((column) => !column.detailOnly), [columns]);
  const visibleColumnCount = showAllColumns || hasDetailOnlyColumns ? visibleColumns.length : maxVisibleColumns;

  return (
    <TableContainer
      ref={tableContainerRef}
      sx={{
        boxShadow: "none",
        overflowX: "auto",
        ...(bodyMaxHeight && { height: bodyMaxHeight, display: "flex", flexDirection: "column" }),
      }}
    >
      {(entriesPerPage && entriesPerPagePosition !== "bottom") || canSearch || actions ? (
        <MDBox display="flex" justifyContent="space-between" alignItems="center" p={3} sx={bodyMaxHeight ? { flexShrink: 0 } : undefined}>
          {entriesPerPage && entriesPerPagePosition !== "bottom" && (
            <MDBox display="flex" alignItems="center">
              <Autocomplete
                disableClearable
                value={pageSize.toString()}
                options={entries}
                onChange={(_, newValue) => {
                  setEntriesPerPage(parseInt(newValue, 10));
                }}
                size="small"
                sx={{ width: "5rem" }}
                renderInput={(params) => <MDInput {...params} />}
              />
              <MDTypography variant="caption" color="secondary">
                &nbsp;&nbsp;entries per page
              </MDTypography>
            </MDBox>
          )}
          {actions && (
            <MDBox display="flex" alignItems="center" gap={1} ml="auto" mr={canSearch ? 2 : 0}>
              {actions}
            </MDBox>
          )}
          {canSearch && (
            <MDBox width="12rem" ml={actions ? 0 : "auto"}>
              <MDInput
                placeholder="Search..."
                value={search}
                size="small"
                fullWidth
                onChange={({ currentTarget }) => {
                  setSearch(currentTarget.value);
                  onSearchChange(currentTarget.value);
                }}
              />
            </MDBox>
          )}
        </MDBox>
      ) : null}
      <MDBox sx={bodyMaxHeight ? { flex: "1 1 auto", minHeight: 0, overflowY: "auto" } : undefined}>
      <Table
        {...getTableProps()}
        style={fixedLayout ? { tableLayout: "fixed", width: "100%" } : { tableLayout: "auto", minWidth: 0 }}
      >
        <MDBox
          component="thead"
          sx={bodyMaxHeight ? ({ palette: { white } }) => ({
            position: "sticky",
            top: 0,
            zIndex: 2,
            backgroundColor: white.main,
          }) : undefined}
        >
          {headerGroups.map((headerGroup, key) => (
            <TableRow key={key} {...headerGroup.getHeaderGroupProps()} >
              {headerGroup.headers.filter((column) => !column.detailOnly).slice(0, visibleColumnCount).map((column, idx) => (
                <DataTableHeadCell
                  key={idx}
                  {...column.getHeaderProps(isSorted && column.getSortByToggleProps ? column.getSortByToggleProps() : undefined)}
                  width={column.width ? column.width : "auto"}
                  align={column.align ? column.align : "left"}
                  sorted={setSortedValue(column)}
                >
                  {column.render("Header")}
                </DataTableHeadCell>
              ))}
            </TableRow>
          ))}
        </MDBox>
        <TableBody {...getTableBodyProps()}>
          {page.map((row, key) => {
            prepareRow(row);
            return (
              <Fragment key={key}>
                <TableRow
                  {...row.getRowProps()}
                  style={{ cursor: 'pointer', ...(getRowStyle ? getRowStyle(row.original) : {}) }}
                  sx={({ palette: { background } }) => ({ '&:hover': { backgroundColor: background.hover } })}
                  onClick={(e) => {
                    if (e.target.closest('button, a, [role="button"]')) return;
                    if (!disableRowDialog) setDialogRow(row);
                    if (onRowClicked) onRowClicked(row, page[key]);
                  }}
                >
                  {row.cells.filter((cell) => !cell.column.detailOnly).slice(0, visibleColumnCount).map((cell, idx) => (
                    <DataTableBodyCell
                      key={idx}
                      noBorder={noEndBorder && rows.length - 1 === key}
                      align={cell.column.align ? cell.column.align : "left"}
                      fixedWidth={fixedLayout}
                      {...cell.getCellProps()}
                    >
                      {cell.render("Cell")}
                    </DataTableBodyCell>
                  ))}
                </TableRow>
              </Fragment>
            );
          })}
          {rows.length === 0 && (
            <TableRow>
              <MDBox component="td" colSpan={visibleColumnCount} py={3} textAlign="center">
                <MDTypography variant="button" color="text" fontWeight="regular">
                  No records found
                </MDTypography>
              </MDBox>
            </TableRow>
          )}
        </TableBody>
      </Table>
      </MDBox>
      <MDBox
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        p={!showTotalEntries && pageOptions.length === 1 && !(entriesPerPage && entriesPerPagePosition === "bottom") ? 0 : 3}
        sx={bodyMaxHeight ? { flexShrink: 0 } : undefined}
      >
        {showTotalEntries && (
          <MDBox mb={{ xs: 3, sm: 0 }}>
            <MDTypography variant="button" color="secondary" fontWeight="regular">
              Showing {entriesStart} to {entriesEnd} of {rows.length} entries
            </MDTypography>
          </MDBox>
        )}
        <MDBox display="flex" alignItems="center" gap={2} ml="auto">
          {entriesPerPage && entriesPerPagePosition === "bottom" && (
            <MDBox display="flex" alignItems="center">
              <Autocomplete
                disableClearable
                value={pageSize.toString()}
                options={entries}
                onChange={(_, newValue) => {
                  setEntriesPerPage(parseInt(newValue, 10));
                }}
                size="small"
                sx={{ width: "5rem" }}
                renderInput={(params) => <MDInput {...params} />}
              />
              <MDTypography variant="caption" color="secondary">
                &nbsp;&nbsp;entries per page
              </MDTypography>
            </MDBox>
          )}
          {pageOptions.length > 1 && (
            <MDPagination
              variant={pagination.variant ? pagination.variant : "gradient"}
              color={pagination.color ? pagination.color : "info"}
            >
              {canPreviousPage && (
                <MDPagination item onClick={() => previousPage()}>
                  <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                </MDPagination>
              )}
              {renderPagination.length > 6 ? (
                <MDBox width="5rem" mx={1}>
                  <MDInput
                    inputProps={{ type: "number", min: 1, max: customizedPageOptions.length }}
                    value={customizedPageOptions[pageIndex]}
                    onChange={handleInputPaginationValue}
                  />
                </MDBox>
              ) : (
                renderPagination
              )}
              {canNextPage && (
                <MDPagination item onClick={() => nextPage()}>
                  <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                </MDPagination>
              )}
            </MDPagination>
          )}
        </MDBox>
      </MDBox>
      {/* Row detail dialog */}
      <Dialog
        open={Boolean(dialogRow)}
        onClose={() => setDialogRow(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        {dialogTitle && <DialogTitle sx={{ pb: 1, fontWeight: 600 }}>{dialogTitle}</DialogTitle>}
        <Divider sx={{ my: 0 }} />
        <DialogContent sx={{ pt: 1 }}>
          {dialogRow && dialogRow.cells.map((cell, idx) => {
            const header = cell.column.Header;
            if (!header || header === "") return null;
            return (
              <MDBox
                key={idx}
                display="flex"
                alignItems="flex-start"
                py={0.75}
                sx={({ borders: { borderColor } }) => ({ borderBottom: `1px solid ${borderColor}`, "&:last-child": { borderBottom: "none" } })}
              >
                <MDTypography variant="caption" fontWeight="bold" sx={({ palette: { dark } }) => ({ minWidth: 160, color: dark.main })}>
                  {header}
                </MDTypography>
                <MDTypography variant="caption" sx={({ palette: { text } }) => ({ color: text.main, wordBreak: "break-word" })}>
                  {cell.render("Cell")}
                </MDTypography>
              </MDBox>
            );
          })}
        </DialogContent>
      </Dialog>
    </TableContainer>
  );
}

// Setting default values for the props of DataTable
DataTable.defaultProps = {
  entriesPerPage: { defaultValue: 10, entries: [5, 10, 15, 20, 25] },
  entriesPerPagePosition: "top",
  canSearch: false,
  showTotalEntries: true,
  pagination: { variant: "gradient", color: "info" },
  isSorted: true,
  noEndBorder: false,
  hideExpandColumn: false,
  onRowClicked: () => {
    return null;
  },
  getRowStyle: null,
  actions: null,
  dialogTitle: null,
  bodyMaxHeight: null,
  disableRowDialog: false,
  fixedLayout: false,
  showAllColumns: false,
};

// Typechecking props for the DataTable
DataTable.propTypes = {
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  entriesPerPagePosition: PropTypes.oneOf(["top", "bottom"]),
  canSearch: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(["contained", "gradient"]),
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "light",
    ]),
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
  hideExpandColumn: PropTypes.bool,
  onRowClicked: PropTypes.func,
  getRowStyle: PropTypes.func,
  actions: PropTypes.node,
  dialogTitle: PropTypes.string,
  bodyMaxHeight: PropTypes.string,
  disableRowDialog: PropTypes.bool,
  fixedLayout: PropTypes.bool,
  showAllColumns: PropTypes.bool,
};

export default DataTable;